import React, { useEffect, useCallback } from 'react';
import { Switch, Route, Redirect } from 'react-router-dom';
import { Location, History } from 'history';
import pearClient from 'api/pearClient';
import { WeaverApiClientInstance } from 'api/weaverApiClientInstance';
import { routes, isValidPath, isReportedTrackingUrl } from 'utils/routeHelper';
import { Config } from 'utils/config/config';
import { saveLastActivity } from 'utils/tokenStorage';
import { isSessionExpired, handleExpiredSession } from 'utils/session';

import IdleLogout from 'components/common/IdleLogout';
import Header from 'components/common/Header';
import Footer from 'components/common/Footer';
import ErrorInterceptor from 'components/common/ErrorInterceptor';
import PageNotFound from 'components/screens/PageNotFound';
import Maintenance from 'components/screens/Maintenance';
import ServerError from 'components/screens/ServerError';
import Login from 'components/screens/Login';
import ForgotPassword from 'components/screens/ForgotPassword';
import SetupAccount from 'components/screens/SetupAccount';
import ResetPassword from 'components/screens/ResetPassword';
import Legal from 'components/screens/Legal';
import ManagePatientAdherencePaginated from 'components/screens/ManagePatientAdherencePaginated';
import ClinicManager from 'components/screens/ClinicManager';
import PatientsSummary from 'components/screens/PatientsSummary';
import Document from 'components/screens/Document';
import SomrystDashboard from 'components/screens/SomrystDashboard';
import MFARegistration from 'components/screens/MFA/Registration';
import MFAValidation from 'components/screens/MFA/Validation';
import PatientDashboardMFE from 'components/screens/PatientDashboardMFE';
import { scopes } from 'utils/scope';
import connectAuthorization from './connectAuthorization';
import './Router.scss';

interface IStateProps {
  userLoggedIn: boolean;
  profile: Nullable<UserProfileDTO>;
}

interface IOwnProps {
  location: Location;
  history: History;
  getUserProfileSuccess: (profile: UserProfileDTO) => void;
}

export type Props = IStateProps & IOwnProps;

const Router = (props: Props) => {
  const { getUserProfileSuccess, location, userLoggedIn, profile, history } =
    props;
  const { pathname } = location;

  const getUserProfile = useCallback(async () => {
    const { data: profile } = await WeaverApiClientInstance.getUserProfile();
    getUserProfileSuccess(profile);
  }, [getUserProfileSuccess]);

  const isInMaintenancePage = useCallback(
    (): boolean => pathname === routes.MAINTENANCE,
    [pathname],
  );

  const isLoadingProfile = (): boolean =>
    !isInMaintenancePage() && userLoggedIn && !profile;

  useEffect(() => {
    window.onbeforeunload = saveLastActivity;
    if (userLoggedIn && !isInMaintenancePage()) {
      getUserProfile();
    }
  }, [userLoggedIn, isInMaintenancePage, getUserProfile]);

  useEffect(() => {
    ErrorInterceptor.dismissAllSnackbar();
    if (
      isReportedTrackingUrl(pathname) &&
      pathname !== routes.MFA_REGISTER &&
      userLoggedIn
    ) {
      pearClient.v2.tracking.trackVisitedPageEvent(pathname);
    }
  }, [pathname, userLoggedIn]);

  const isExpired = isSessionExpired();
  handleExpiredSession(isExpired);
  if (isLoadingProfile()) return null;
  return (
    <div styleName="weaver-container">
      <Header history={history} location={location} />
      <div styleName="wrapper">
        <main styleName="main-content">
          {!isValidPath(location.pathname) ||
          location.pathname === routes.INVALID_PATH ? (
            // is this even needed above?
            <Switch>
              <Route path={routes.INVALID_PATH} component={PageNotFound} />
              <Redirect to={routes.INVALID_PATH} />
            </Switch>
          ) : (
            <>
              {userLoggedIn && (
                <div data-testid="idle-logout">
                  <IdleLogout />
                </div>
              )}
              <Switch>
                <Route path={routes.MFA_REGISTER} component={MFARegistration} />
                <Route path={routes.MFA_VALIDATION} component={MFAValidation} />
                <Route path={routes.LOG_IN} component={Login} />
                <Route
                  path={routes.FORGOT_PASSWORD}
                  component={ForgotPassword}
                />
                <Route
                  path={`(${routes.CLINICIAN_SETUP_ACCOUNT}|${routes.SETUP_ACCOUNT})`}
                  component={SetupAccount}
                />
                <Route path={routes.RESET_PASSWORD} component={ResetPassword} />
                <Route
                  path={routes.RESET_DFU}
                  render={() => {
                    window.location.assign(
                      `${Config.HYLIA_ASSETS_URL}/legal-content/reset/clinician-directions-for-use.pdf`,
                    );
                    return null;
                  }}
                />
                <Route
                  path={routes.RESET_O_DFU}
                  render={() => {
                    window.location.assign(
                      `${Config.HYLIA_ASSETS_URL}/legal-content/reset-o/clinician-directions-for-use.pdf`,
                    );
                    return null;
                  }}
                />
                <Route
                  path={routes.RESET_0_DFU}
                  render={() => {
                    window.location.assign(
                      `${Config.HYLIA_ASSETS_URL}/legal-content/reset-o/clinician-directions-for-use.pdf`,
                    );
                    return null;
                  }}
                />
                <Route
                  path={routes.RESET_PATIENT_PRIVACY}
                  render={() => {
                    window.location.assign(
                      `${Config.HYLIA_ASSETS_URL}/legal-content/reset/privacy-policy.pdf`,
                    );
                    return null;
                  }}
                />
                <Route
                  path={routes.RESET_O_PATIENT_PRIVACY}
                  render={() => {
                    window.location.assign(
                      `${Config.HYLIA_ASSETS_URL}/legal-content/reset-o/privacy-policy.pdf`,
                    );
                    return null;
                  }}
                />
                <Route
                  path={routes.RESET_PATIENT_TERMS}
                  render={() => {
                    window.location.assign(
                      `${Config.HYLIA_ASSETS_URL}/legal-content/reset/terms-of-service.pdf`,
                    );
                    return null;
                  }}
                />
                <Route
                  path={routes.RESET_O_PATIENT_TERMS}
                  render={() => {
                    window.location.assign(
                      `${Config.HYLIA_ASSETS_URL}/legal-content/reset-o/terms-of-service.pdf`,
                    );
                    return null;
                  }}
                />
                <Redirect exact from={routes.ROOT} to={routes.LOG_IN} />
                <Route path={routes.LEGAL} component={Legal} />
                <Route
                  path={routes.DOCUMENTS}
                  render={connectAuthorization(Document)}
                />
                <Route
                  path={routes.MANAGE_LOCATIONS}
                  render={connectAuthorization(ClinicManager, {
                    requiredScope: scopes.LOCATION_READ,
                  })}
                />
                <Route
                  path={routes.MANAGE_CLINICIANS}
                  render={connectAuthorization(ClinicManager, {
                    requiredScope: scopes.MANAGE_CLINICIANS_LIST,
                  })}
                />
                <Route
                  path={routes.MANAGE_PATIENTS}
                  render={connectAuthorization(ClinicManager, {
                    requiredScope: scopes.MANAGE_PATIENTS_LIST,
                  })}
                />
                <Route
                  path={routes.MANAGE_PATIENT_ADHERENCE}
                  render={connectAuthorization(
                    ManagePatientAdherencePaginated,
                    {
                      requiredScope: scopes.PATIENT_ADHERENCE_LIST,
                    },
                  )}
                />
                <Route
                  path={routes.PATIENT_DASHBOARD_IFRAME}
                  render={connectAuthorization(PatientDashboardMFE, {
                    requiredScope: scopes.PATIENT_READ,
                  })}
                />
                <Route
                  exact
                  path={routes.SOMRYST_DASHBOARD}
                  render={connectAuthorization(SomrystDashboard, {
                    requiredScope: scopes.PATIENT_READ,
                  })}
                />
                <Route
                  exact
                  path={routes.PATIENT_SUMMARY}
                  render={connectAuthorization(PatientsSummary, {
                    requiredScope: scopes.PATIENT_SUMMARY_LIST,
                  })}
                />
                <Route path={routes.INVALID_PATH} component={PageNotFound} />
                <Route path={routes.MAINTENANCE} component={Maintenance} />
                <Route path={routes.WHOOPS} component={ServerError} />
              </Switch>
            </>
          )}
        </main>
      </div>
      <Footer />
    </div>
  );
};

export default Router;
